import { useQuery } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

import { GET_COMMUNITY_GROUPS_WITH_COMMUNITIES } from './graphql';

/**
 * Community interface for Guide navigation
 */
export interface GuideCommunity {
  id: string;
  name: string;
  communityGroupId: string;
  userRole?: string | null;
  roles?: string[] | null;
  logoUrl?: string | null;
  optionalMetadata?: {
    bigId?: string | null;
  } | null;
}

/**
 * Community Group with nested communities
 */
export interface CommunityGroupWithCommunities {
  id: string;
  name: string;
  description: string;
  userRole?: string | null;
  roles?: string[] | null;
  communities: GuideCommunity[];
}

/**
 * Query response interface
 */
interface CommunityGroupsWithCommunitiesQueryResponse {
  communityGroupsWithCommunities: {
    communityGroups: CommunityGroupWithCommunities[];
    isAdmin: boolean;
  };
}

/**
 * Custom hook for fetching community groups with nested communities
 * Uses the optimized BFF query that fetches all data in a single request
 *
 * @returns Community groups with nested communities, loading state, errors, and refetch function
 */
export function useCommunityGroupsWithCommunities() {
  const { data, loading, error, refetch } = useQuery<CommunityGroupsWithCommunitiesQueryResponse>(GET_COMMUNITY_GROUPS_WITH_COMMUNITIES, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const communityGroups = data?.communityGroupsWithCommunities.communityGroups || [];
  const isAdmin = data?.communityGroupsWithCommunities.isAdmin || false;

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  return {
    data: communityGroups,
    isAdmin,
    loading,
    errors,
    refetch,
  };
}
